import { useEffect } from "react";
import { GetServerSideProps } from "next";
import { useTranslation } from "next-i18next";
import { fetchUnderlyingOverview } from "src/api/server/underlying-overview/underlying-overview";
import { Section } from "src/components/base";
import { BlogTeaserModule } from "src/components/modules/blog-teaser-module";
import { CtaModule } from "src/components/modules/cta-module/cta-module";
import { ImageVideoModule } from "src/components/modules/image-video-module/image-video-module";
import { UnderlyingListModule } from "src/components/modules/underlying-lists-module/underlying-list-module";
import { VotingModule } from "src/components/modules/voting-module/voting-module";
import { StockSearch } from "src/components/pages/underlying-overview/stock-search/stock-search";
import { IUnderlyingOverviewProps } from "src/components/pages/underlying-overview/underlying-overview.types";
import { TOP_MARGIN_CONTENT_APP } from "src/consts/styles";
import { useContentPageVisitTracking } from "src/hooks/use-content-page-visit-tracking";
import { serverSideTranslations } from "src/i18n/server-side-translations";
import { useAuthModalStore } from "src/stores/auth-modal-store";
import { useUserStore } from "src/stores/user-store";
import { IBaseParam } from "src/types/common";
import { EnumHorizontalAlignment } from "src/types/common-enum";
import { addServerTiming } from "src/utils/nextjs/add-server-timing";
import { useRouterCaller } from "src/utils/router/use-router-caller";

const UNDERLYING_LIST_SECTION_ID = "underlyinglist";
const VOTING_SECTION_ID = "voting";
const BLOG_TEASER_SECTION_ID = "blogteaser";

function Page({ data, dict }: IUnderlyingOverviewProps) {
    const { isCallerContentApp } = useRouterCaller();
    useContentPageVisitTracking({});
    const { isLoggedIn } = useUserStore();
    const { t } = useTranslation("underlying-overview");
    const { setReturnUrl } = useAuthModalStore();

    useEffect(() => {
        setReturnUrl(window.location.pathname);
    }, [setReturnUrl]);

    return (
        <>
            <Section mt={isCallerContentApp ? TOP_MARGIN_CONTENT_APP : [4, 7]}>
                <StockSearch title={dict.introHeadline} description={dict.introDescription} placeholder={t("stock-search-placeholder")} titleAsH1 />
            </Section>

            {data.multiVoteModule?.votes?.length && (
                <Section pt={[10, 14]} id={VOTING_SECTION_ID}>
                    <VotingModule title={data.multiVoteModule.title} votes={data.multiVoteModule.votes} />
                </Section>
            )}

            <Section pt={[10, 14]} id={UNDERLYING_LIST_SECTION_ID}>
                <UnderlyingListModule
                    title={dict.underlyingListHeadline}
                    description={dict.underlyingListDescription}
                    underlyingLists={data.underlyingLists}
                    isOverlayEnabled={data.isUnderlyingListOverlayEnabled}
                    overlayTitle={dict.underlyingListOverlayHeadline}
                    overlayDescription={dict.underlyingListOverlayDescription}
                />
            </Section>

            {data.ctaModule && (
                <Section mt={[10, 14]}>
                    <CtaModule {...data.ctaModule} />
                </Section>
            )}

            <Section pt={[10, 14]} mb={!data.imageVideoModule ? [32, 56] : undefined} id={BLOG_TEASER_SECTION_ID}>
                <BlogTeaserModule
                    titleAsH1={false}
                    title={dict.blogTeaserHeadline}
                    headerAlignment={EnumHorizontalAlignment.Left}
                    items={data.blogTeaserSectionPosts ?? []}
                />
            </Section>

            {isLoggedIn && data.imageVideoModule && (
                <Section mt={[10, 14]} mb={[32, 56]}>
                    <ImageVideoModule {...data.imageVideoModule} isFirstModule={false} />
                </Section>
            )}

            {!isLoggedIn && data.imageVideoModuleLoggedOut && (
                <Section mt={[10, 14]} mb={[32, 56]}>
                    <ImageVideoModule {...data.imageVideoModuleLoggedOut} isFirstModule={false} />
                </Section>
            )}
        </>
    );
}

type IParam = IBaseParam;

export const getServerSideProps: GetServerSideProps<IUnderlyingOverviewProps, IParam> = async context => {
    const [props, translations] = await addServerTiming(context.res, "ssprops", async () => {
        const props = await fetchUnderlyingOverview(context);
        const translations = await serverSideTranslations(context.locale as string, ["common", "underlying-overview", "date-range", "voting-module"]);

        return [props, translations];
    });

    if (!props) {
        return { notFound: true };
    }

    return {
        props: {
            ...props,
            ...translations,
        },
    };
};

export default Page;
